import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const OrganizationContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default organization config (Burston/fallback)
const DEFAULT_ORG = {
    id: "default",
    name: "Residents Plus",
    slug: "default",
    tagline: "Premium Lifestyle Membership",
    branding: {
        primary_color: "163 68% 18%",
        secondary_color: "160 55% 27%",
        accent_color: "4 68% 68%",
        background_color: "45 23% 95%",
        logo_url: null,
        heading_font: "Merriweather",
        body_font: "Mulish"
    },
    contact: {},
    features: {
        activities: true,
        events: true,
        offers: true,
        tips: true,
        cafe_menu: true,
        partners: true
    },
    terminology: {
        activities: "Activities",
        events: "Events",
        offers: "Offers",
        tips: "Tips",
        members: "Residents",
        credits: "Credits"
    },
    membership_tiers: [
        { name: "Essential", price: 499, annual_credits: 15, description: "Perfect for occasional participation", is_active: true },
        { name: "Premium", price: 995, annual_credits: 30, description: "Best value for active members", is_active: true }
    ],
    manager_message: "",
    demo_mode: true
};

// Helper function to extract org slug from hostname
function getOrgSlugFromHostname() {
    const hostname = window.location.hostname;
    
    // Check for query param first (for testing/override)
    const urlParams = new URLSearchParams(window.location.search);
    const orgParam = urlParams.get('org');
    if (orgParam) {
        return orgParam;
    }
    
    // Handle preview environment subdomains (e.g., elysian.residents-plus.preview.emergentagent.com)
    // Pattern: {org}.{app-name}.preview.emergentagent.com
    if (hostname.includes('preview.emergentagent.com')) {
        const parts = hostname.split('.');
        // Check if there's a subdomain before the app name
        // e.g., ['elysian', 'residents-plus', 'preview', 'emergentagent', 'com'] = 5 parts
        // Without subdomain: ['residents-plus', 'preview', 'emergentagent', 'com'] = 4 parts
        if (parts.length >= 5) {
            const potentialOrg = parts[0];
            // Exclude common non-org prefixes
            if (!['www', 'api', 'app', 'admin'].includes(potentialOrg)) {
                return potentialOrg;
            }
        }
        // No subdomain in preview environment
        return null;
    }
    
    // Handle production subdomains (e.g., elysian.residentsplus.com)
    const parts = hostname.split('.');
    if (parts.length >= 3 && parts[0] !== 'www') {
        return parts[0];
    }
    
    // Return null to indicate no specific org detected
    return null;
}

export function OrganizationProvider({ children }) {
    const [organization, setOrganization] = useState(DEFAULT_ORG);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSubdomainOrg, setIsSubdomainOrg] = useState(false);

    const fetchOrganization = useCallback(async (slug, fromSubdomain = false) => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/organization/by-slug/${slug}`);
            setOrganization(response.data);
            applyBranding(response.data.branding);
            setIsSubdomainOrg(fromSubdomain);
            setError(null);
        } catch (err) {
            console.error('Error fetching organization:', err);
            // Fall back to default
            setOrganization(DEFAULT_ORG);
            applyBranding(DEFAULT_ORG.branding);
            setIsSubdomainOrg(false);
            setError('Organization not found, using defaults');
        } finally {
            setLoading(false);
        }
    }, []);

    // Set organization directly (used when selecting from Gateway)
    const setOrganizationData = useCallback((orgData) => {
        if (orgData) {
            setOrganization(orgData);
            applyBranding(orgData.branding);
        }
    }, []);

    const applyBranding = (branding) => {
        if (!branding) return;
        
        const root = document.documentElement;
        
        // Apply CSS custom properties for colors
        if (branding.primary_color) {
            root.style.setProperty('--primary', branding.primary_color);
            root.style.setProperty('--ring', branding.primary_color);
        }
        if (branding.secondary_color) {
            root.style.setProperty('--secondary', branding.secondary_color);
        }
        if (branding.accent_color) {
            root.style.setProperty('--accent', branding.accent_color);
        }
        if (branding.background_color) {
            root.style.setProperty('--background', branding.background_color);
        }
        
        // Apply fonts
        if (branding.heading_font) {
            root.style.setProperty('--font-heading', `'${branding.heading_font}', Georgia, serif`);
        }
        if (branding.body_font) {
            root.style.setProperty('--font-body', `'${branding.body_font}', -apple-system, sans-serif`);
        }
    };

    const refreshOrganization = async () => {
        const slug = getOrgSlugFromHostname();
        await fetchOrganization(slug);
    };

    useEffect(() => {
        // First check for subdomain or query param (takes priority)
        const slugFromUrl = getOrgSlugFromHostname();
        if (slugFromUrl) {
            // Auto-set organization based on subdomain/query param
            fetchOrganization(slugFromUrl, true);
            return;
        }
        
        // Then check if there's a selected org in localStorage (from Canopy Gateway)
        const storedOrg = localStorage.getItem('selectedOrg');
        if (storedOrg) {
            try {
                const parsed = JSON.parse(storedOrg);
                // Use the org id/slug to fetch full org data from backend
                if (parsed.id) {
                    fetchOrganization(parsed.id, false);
                    return;
                }
            } catch (e) {
                console.error('Error parsing stored org:', e);
            }
        }
        
        // No org detected - will show gateway to select
        setLoading(false);
    }, [fetchOrganization]);

    // Helper to get terminology with fallback
    const getTerm = (key) => {
        return organization?.terminology?.[key] || DEFAULT_ORG.terminology[key] || key;
    };

    // Helper to check if feature is enabled
    const isFeatureEnabled = (feature) => {
        return organization?.features?.[feature] ?? true;
    };

    // Helper to get active membership tiers
    const getActiveTiers = () => {
        return (organization?.membership_tiers || []).filter(t => t.is_active);
    };

    const value = {
        organization,
        setOrganization: setOrganizationData,
        loading,
        error,
        isSubdomainOrg,
        refreshOrganization,
        fetchOrganization,
        getTerm,
        isFeatureEnabled,
        getActiveTiers,
        branding: organization?.branding || DEFAULT_ORG.branding,
        contact: organization?.contact || {},
        features: organization?.features || DEFAULT_ORG.features,
        terminology: organization?.terminology || DEFAULT_ORG.terminology,
    };

    return (
        <OrganizationContext.Provider value={value}>
            {children}
        </OrganizationContext.Provider>
    );
}

export function useOrganization() {
    const context = useContext(OrganizationContext);
    if (!context) {
        throw new Error('useOrganization must be used within an OrganizationProvider');
    }
    return context;
}

export default OrganizationContext;
